declare const nodeHandleBrand: unique symbol;
declare const edgeHandleBrand: unique symbol;

/** Process-local addresses. Neither type is a persisted project identity. */
export type NodeHandle = number & { readonly [nodeHandleBrand]: true };
export type EdgeHandle = number & { readonly [edgeHandleBrand]: true };

export const NO_RUNTIME_HANDLE = 0;
export const NO_NODE_HANDLE = NO_RUNTIME_HANDLE as NodeHandle;
export const MAX_RUNTIME_HANDLE = 0xffff_ffff;

export interface RuntimeHandleScope {
  allocateNode(): NodeHandle;
  allocateEdge(): EdgeHandle;
  assertSameScope(other: RuntimeHandleScope): void;
  close(): void;
}

/**
 * One allocator per application realm, shared by every project scope. A single
 * monotonic sequence prevents stale callbacks and foreign-project handles from
 * aliasing newly allocated nodes, including after a project is closed.
 *
 * This stores two counters, not a string dictionary or a second graph. Zero is
 * reserved for absent typed-array entries. Exhaustion throws before wraparound.
 */
export class RuntimeHandleAllocator {
  #last = NO_RUNTIME_HANDLE;
  readonly #limit: number;

  constructor(limit = MAX_RUNTIME_HANDLE) {
    if (!Number.isInteger(limit) || limit < 1 || limit > MAX_RUNTIME_HANDLE)
      throw new RangeError('Invalid runtime handle limit');
    this.#limit = limit;
  }

  createScope(): RuntimeHandleScope {
    return new HandleScope(() => {
      if (this.#last === this.#limit)
        throw new RangeError('Runtime handle space exhausted; reopen the application');
      return ++this.#last;
    });
  }
}

/** All graphs that share node objects within one project use the same scope. */
class HandleScope implements RuntimeHandleScope {
  #closed = false;
  readonly #allocate: () => number;

  constructor(allocate: () => number) {
    this.#allocate = allocate;
  }

  allocateNode(): NodeHandle {
    this.#assertOpen();
    return this.#allocate() as NodeHandle;
  }

  allocateEdge(): EdgeHandle {
    this.#assertOpen();
    return this.#allocate() as EdgeHandle;
  }

  /** Call before merging graphs or resolving imported cross-graph bindings. */
  assertSameScope(other: RuntimeHandleScope): void {
    this.#assertOpen();
    if (this !== other)
      throw new TypeError('Runtime graphs belong to different projects');
  }

  close(): void {
    this.#closed = true;
  }

  #assertOpen(): void {
    if (this.#closed) throw new Error('Runtime handle scope is closed');
  }
}

const applicationHandles = new RuntimeHandleAllocator();

/** One scope per hydrated project, shared by its templates, instances and drafts. */
export function createRuntimeHandleScope(): RuntimeHandleScope {
  return applicationHandles.createScope();
}
